/**
 * Realtime wire protocol (spec §7) — VERBATIM COPY of the worker's
 * `cf-workers/realtime/src/protocol.ts`. The spec §7 + that file are the
 * canonical source; cf-workers is a separate package, so this is a second
 * physical copy. If you change a type in one, change it in the other (and the
 * spec) too. Keep them byte-for-byte identical below the header.
 *
 * Frame taxonomy:
 *   - Text frames are JSON (all message types below).
 *   - Binary frames occur ONLY between `snapshot_begin` and `snapshot_end`:
 *     byte-slices of one gzip stream whose inflated content is RtSnapshotBody.
 */

// ─────────────────────────────────────────────────────────────────────────────
// §7.1 Connection
// ─────────────────────────────────────────────────────────────────────────────

export type Feed = 'flow' | 'darkpool' | 'prices';
export type FeedClass = 'smart' | 'full';

export const SUBPROTOCOL = 'if-rt-v1';
export const TOKEN_SUBPROTOCOL_PREFIX = 'fbase.';

// ─────────────────────────────────────────────────────────────────────────────
// §7.2 Server → client messages
// ─────────────────────────────────────────────────────────────────────────────

export type Tier = 'paid' | 'free';

export interface RtHello {
  t: 'hello';
  feed: 'flow' | 'darkpool' | 'prices';
  tier: Tier;
  sessionDate: string | null; // YYYY-MM-DD ET; null for prices and for an empty feed (fresh deploy pre-backfill)
  headSeq: number; // 0 for prices
  resume: 'full' | 'diff' | 'none'; // what follows: full snapshot, diff delta, or nothing (client was current).
  // prices is always { sessionDate: null, headSeq: 0, resume: 'full' } + inline snapshot
  serverTime: number; // epoch ms
}

export interface RtSnapshotBegin {
  t: 'snapshot_begin';
  seq: number;
  parts: number;
  format: 'flow.v1' | 'darkpool.v1';
}
// ...then exactly `parts` binary frames: byte-slices (≤512 KiB) of ONE gzip stream whose
// inflated content is RtSnapshotBody JSON. Client: concatenate → DecompressionStream('gzip') → parse.
export interface RtSnapshotEnd {
  t: 'snapshot_end';
  seq: number;
}

export interface RtSnapshotBody {
  // flow.v1
  rows: FlowRow[]; // full visible session, ascending seq
  tickers: Record<string, FlowTickerInfo>;
} // darkpool.v1: { rows: DarkPoolRow[] }

export interface DarkPoolSnapshotBody {
  rows: DarkPoolRow[];
}

export interface RtInlineSnapshot {
  // prices only
  t: 'snapshot';
  quotes: PriceQuote[];
}

export interface RtDelta {
  t: 'delta';
  sessionDate: string;
  seq: number; // seq of the LAST row in this frame
  rows: FlowRow[] | DarkPoolRow[]; // ascending
  tickers?: Record<string, FlowTickerInfo>; // only new/changed tickers (flow only)
}

export interface RtSessionReset {
  t: 'session_reset';
  sessionDate: string;
} // client clears store; fresh deltas follow

export interface RtPrices {
  t: 'prices';
  ts: number;
  quotes: PriceQuote[];
}
export interface RtError {
  t: 'error';
  code: number;
  msg: string;
}
export interface RtPong {
  t: 'pong';
}

export type ServerMessage =
  | RtHello
  | RtSnapshotBegin
  | RtSnapshotEnd
  | RtInlineSnapshot
  | RtDelta
  | RtSessionReset
  | RtPrices
  | RtError
  | RtPong;

// ─────────────────────────────────────────────────────────────────────────────
// §7.4 Data shapes
// ─────────────────────────────────────────────────────────────────────────────

// FLOW — trade-level (one per print/group). Field names match the existing GraphQL
// fragment `optionFlowAllSidesTickerFields` (src/gql/fragments/tickerFields.ts) so the
// client adapter is a pass-through for these:
export interface FlowRow {
  seq: number;
  id: string; // deterministic content-derived eventId (UUIDv8), shared by live ingest, Postgres, and Hasura backfill
  createdAt: string; // stamped by the DO at ingest arrival (ISO) — mirrors the SQL views' insert-time semantics and drives release_at; backfilled rows keep their Hasura values
  exchangeTimestamp: string; // first print, epoch ns
  lastExchangeTimestamp?: string | null; // last print, epoch ns; absent/null when not recorded
  date: string;
  ticker: string;
  cp: 'Call' | 'Put'; // the DB/view value (Right::as_okey_cp), not "C"/"P"
  strike: number;
  expireYear: number;
  expireMonth: number;
  expireDay: number;
  price: number;
  size: number;
  premium: number;
  spot: number;
  priceExternalApi: number | null;
  tradeSide: string;
  orderType: string;
  isFilteredFlow: boolean; // D25: true ⇒ smart/flow row; only feedClass=full sockets also receive false rows
  openInterest: number;
  impliedVol: number;
  delta: number;
  gamma: number;
  rho: number;
  theta: number;
  vega: number;
  yearsToExpiry: number;
  otmPercent: number;
  nbboAsk: number;
  nbboBid: number;
  currentDailyVolume: number;
  // Null when the upstream enrichment source cannot calculate the value.
  vanna: number | null;
  vomma: number | null;
  charm: number | null;
  zomma: number | null;
  color: number | null;
  speed: number | null;
  veta: number | null;
  vera: number | null;
  ultima: number | null;
  lambda: number | null;
  probTouch: number | null;
  riskFreeRate: number | null;
  nbboMidIv: number | null;
}

// FLOW — ticker-level, upserted separately (D2/D23 bandwidth split). These are the
// remaining fragment fields; the adapter re-joins them onto each row for the table:
export interface FlowTickerInfo {
  ticker: string;
  name: string | null;
  sector: string | null;
  marketCap: number | null;
  dailyVolume: number; // per-ticker Σ size over baseline rows this session, DO-accumulated (§6.1) — daily_volume view parity; NOT the per-contract currentDailyVolume
  weeklyTotalSize: number;
  weeklyTotalTrades: number;
  weeklyTotalPremium: number;
  weeklyTotalOpenInterest: number;
  weeklyTotalImpliedVol: number;
  avgContractsPerDay: number;
  avgContractsPerTrade: number;
  avgImpliedVolPerTrade: number;
  avgOpenInterestPerDay: number;
  avgOpenInterestPerTrade: number;
  avgPremiumPerDay: number;
  avgPremiumPerTrade: number;
  avgTradesPerDay: number;
}

// DARK POOL — the object equityflow already builds in mapTradeDataToDatabase
// (processTradesWorker.js), passed through unchanged, plus seq:
export interface DarkPoolRow {
  seq: number;
  [field: string]: unknown;
} // concrete fields per that mapper

// PRICES
export interface PriceQuote {
  symbol: string;
  price: number;
  ts: number; // last trade price + epoch ms
  dayVolume?: number | null; // reserved — not currently populated by the price producer (§17.6 R2)
  bar1m: {
    // Canonical klinecharts KLineData shape — byte-identical to the REST /prices/history
    // bars (Rust `Bar` Serialize), so it feeds applyNewData/updateData verbatim.
    timestamp: number; // ms; interval-aligned bar open (klinecharts reads this)
    start_ns: number; // ns; un-narrowed source precision (klinecharts ignores it)
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
    complete: boolean;
    truncated: boolean;
  } | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// §7.5 Client → server messages
// ─────────────────────────────────────────────────────────────────────────────

export interface RtSub {
  t: 'sub';
  symbols: string[];
} // PRICES ONLY — replaces the socket's symbol set; server replies with an inline snapshot for newly added symbols (§6.3)
export interface RtUnsub {
  t: 'unsub';
  symbols: string[];
} // PRICES ONLY
export interface RtPing {
  t: 'ping';
} // only after 90s silence; answered by auto-response

export type ClientMessage = RtSub | RtUnsub | RtPing;

// ─────────────────────────────────────────────────────────────────────────────
// §7.6 Close codes
// ─────────────────────────────────────────────────────────────────────────────

export const CloseCode = {
  DEPLOY_RESTART: 1012, // reconnect with jitter, resume
  BAD_REQUEST: 4400, // do not retry; fall back to polling
  UNAUTHENTICATED: 4401, // refresh token, reconnect once; then fallback
  FORBIDDEN: 4403, // do not retry; polling/paywall path
  REAUTH: 4408, // reauth interval reached (4h) — silent reconnect w/ fresh token + resume
  OVER_LIMIT: 4429, // over subscription limits; do not retry with same params
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// §7.7 Internal types (worker ↔ DO and DO ↔ DO RPC; never sent to browsers)
// ─────────────────────────────────────────────────────────────────────────────

export interface FlowIngestRow extends Record<string, unknown> {
  /**
   * Deterministic content-derived identity (time-ordered UUIDv8), computed by the producer
   * from the trade's content and written identically to Postgres and this live push. The
   * same trade reproduces the same eventId on replay/refetch — the dedupe + reconciliation key.
   */
  eventId: string;
  /** Legacy/ignored. eventId now carries the dedupe identity; groupKey is no longer required. */
  groupKey?: string;
}

export type FlowIngestBatch = {
  source: 'optionflow';
  batchId: number;
  rows: FlowIngestRow[];
}; // OutputRow JSON + isFilteredFlow
export type DarkPoolIngestBatch = {
  source: string;
  batchId?: number; // optional monotonic id for gap detection (§17.8 P1)
  prints: Record<string, unknown>[];
};
export type PricesIngestBatch = { ts: number; quotes: PriceQuote[] };

export interface BroadcastInstruction {
  // publisher → relay RPC; also the publisher's own broadcast input
  kind: 'delta' | 'session_reset' | 'prices' | 'close_all';
  tier: 'paid' | 'free' | 'all';
  sessionDate: string | null;
  seq: number; // 0 for prices / session_reset
  rows?: FlowRow[] | DarkPoolRow[]; // kind 'delta'
  tickers?: Record<string, FlowTickerInfo>;
  quotes?: PriceQuote[]; // kind 'prices'
  ts?: number; // kind 'prices' — the batch sample time (RtPrices.ts)
}
// The receiving holder (publisher or relay) runs the SAME per-socket frame builder over an
// instruction: filter rows by the socket's tier/tickers/feedClass, serialize, send. Relays never
// re-derive tiers — they trust the instruction's tier tag.

export interface ResumePayload {
  mode: 'full' | 'diff' | 'none';
  sessionDate: string | null;
  seq: number; // target head seq (client cursor after applying)
  snapshotSeq?: number; // mode 'full': seq the snapshot chunks were built at (may be < seq → catch-up delta in `rows`)
  chunks?: ArrayBuffer[]; // mode 'full', flow/darkpool: pre-gzipped snapshot chunks (≤512 KiB each)
  rows?: FlowRow[] | DarkPoolRow[]; // mode 'diff', or catch-up rows for a cached 'full'
  tickers?: Record<string, FlowTickerInfo>;
  quotes?: PriceQuote[]; // prices 'full'
}

export interface FeedStatus {
  sessionDate: string | null;
  headSeq: number;
  socketCounts: { paid: number; free: number };
  relayCounts: number;
  lastIngestAt: number | null;
  // droppedNoEventId/droppedStaleDate break `dropped` down by reason (flow feed only),
  // so a nonzero drop count self-identifies without a Workers Logs dig.
  ingestTotals: {
    accepted: number;
    duplicates: number;
    dropped: number;
    droppedNoEventId?: number;
    droppedStaleDate?: number;
  };
  backfilled: boolean;
  repairs?: number; // count of watermark-triggered repairs this session (§17.10 E)
}

// ─────────────────────────────────────────────────────────────────────────────
// Connect-time context passed from the stateless worker to the DO via headers.
// (Not sent to browsers.)
// ─────────────────────────────────────────────────────────────────────────────

export interface ConnectContext {
  tier: Tier;
  userId: string | null;
  expiresAt: number; // epoch ms, connectTime + REAUTH_INTERVAL_MS
}

// Per-socket attachment serialized via ctx.serializeAttachment(). Survives
// hibernation, so all per-connection state that broadcast/demand loops need
// must live here (never only in memory).
export interface SocketAttachment {
  tier: Tier;
  userId: string | null;
  expiresAt: number;
  tickers: string[] | null; // flow/darkpool per-socket ticker filter
  feedClass: FeedClass; // flow only; darkpool/prices ignore
  symbols: string[] | null; // prices per-socket symbol set
  feed?: Feed; // set by the worker (x-rt-feed) only on relay connects
  ready?: boolean; // false during the handshake; broadcastLocal skips not-ready sockets (§17.1 L1)
  acceptedAt?: number; // epoch ms of acceptWebSocket — a still-not-ready socket past a bound is a zombie (§17.10.3)
}

// Parsed `?since=<date>:<seq>` resume cursor (flow/darkpool).
export interface SinceCursor {
  date: string;
  seq: number;
}
